/**
 * AppServices context — the dependency-injection seam.
 *
 * The web and desktop apps construct the concrete PlatformIO + EngineBinding +
 * JsonClient and provide them here. Feature components consume ONLY this context
 * (never Tauri/WASM directly) — contracts/client-integration.md.
 */
import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { JsonClient, PlatformIO, QueueStore } from "@genz/platform";
import type { EngineBinding } from "@genz/engine-bind";
import type { QueueManager } from "../queue/manager.js";
import type { TelemetryClient } from "../telemetry/client.js";

/**
 * Minimal logger interface for the DI seam (Task 6 — client observability).
 * Satisfied by `ClientLogger` from `@genz/shared-ui/observability/logger`.
 * Optional so existing tests and the web-only build don't need changes.
 */
/** What the Update button shows while an update downloads and installs. */
export type UpdateStatus = { phase: "downloading"; pct: number } | { phase: "installing" };

export interface AppLogger {
  start(): void;
  stop(): void;
  installGlobalHandlers(): void;
  setCustomer(id: string): void;
  setRoute(route: string): void;
  observeRequest(r: { method: string; urlPath: string; status?: number; durationMs?: number; ok: boolean; error?: string }): void;
  log(level: string, message: string, ctx?: Record<string, unknown>): void;
  breadcrumb(category: string, message: string, data?: Record<string, unknown>): void;
  flush(): Promise<void>;
  /** Recent device-local error/warn events — shown in the Error log irrespective of
   *  the cloud (offline / cloud down / not-yet-synced). Optional for the DI seam. */
  recentLocalErrors?(): Promise<import("@genz/platform").ClientLogRow[]>;
  /** Drop buffered + locally-retained events on sign-out, so the previous account's
   *  activity can't flush under the next account's token. Optional for the DI seam. */
  purgeBuffers?(): Promise<void>;
  /** Flush immediately rather than waiting for the 30s timer — called on the
   *  session-gained edge so pre-login failures reach the cloud at once (AUD-4 IMP-5). */
  flushNow?(): Promise<void>;
  /** Buffered-but-not-yet-accepted event count, so the Observability panel can tell
   *  "nothing was logged" apart from "nothing was delivered". */
  undeliveredCount?(): Promise<number>;
  /** F-10: durable logging resumes once a session exists again. `purgeBuffers()` suspends
   *  it, so sign-out never writes into a store its own purge is clearing. Optional for the
   *  DI seam — a logger without it simply never suspended. */
  resumeDurableWrites?(): void;
}

export interface AppServices {
  /**
   * WS-ERR H-2 — drop every buffer that belongs to the account that is leaving: the telemetry
   * buffer AND the client-log buffer, in one call.
   *
   * Each sign-out site used to build its own `purgeBuffers` from the LOGGER alone, so the
   * telemetry buffer survived a deliberate sign-out — and `start()` (which `bootstrap.ts` calls
   * on the session-gained edge) ends with a flush, so the next account's token carried the
   * previous account's events. One composed function, wired once in `bootstrap.ts`, is what
   * stops the three call sites drifting apart again.
   */
  purgeAccountBuffers(): Promise<void>;
  io: PlatformIO;
  engine: EngineBinding;
  client: JsonClient;
  queueStore: QueueStore;
  queue: QueueManager;
  telemetry: TelemetryClient;
  appVersion: string;
  /**
   * Trigger a manual self-update check. Provided by the DESKTOP app only (it
   * wires the Tauri updater plugin); web omits it, so the "Check for updates"
   * control never renders there.
   */
  checkForUpdates?: (onStatus?: (status: UpdateStatus) => void) => Promise<void>;
  /**
   * Client observability logger (Task 6). Constructed in main.tsx at boot and
   * started here via AppServicesProvider. Optional — services without it skip
   * the observability wiring (tests, minimal builds).
   */
  logger?: AppLogger;
}

const AppServicesContext = createContext<AppServices | null>(null);

export function AppServicesProvider(props: { services: AppServices; children: ReactNode }) {
  const { logger } = props.services;

  // Start the logger and install global error handlers once at mount.
  // Mirrors the pattern in TelemetryClient: start() subscribes to connectivity,
  // stop() cleans up on unmount.
  useEffect(() => {
    if (!logger) return;
    logger.installGlobalHandlers();
    logger.start();
    return () => logger.stop();
  }, [logger]);

  return <AppServicesContext.Provider value={props.services}>{props.children}</AppServicesContext.Provider>;
}

/** The services when a provider is mounted, else null: for a control a standalone editor also renders. */
export function useOptionalServices(): AppServices | null {
  return useContext(AppServicesContext);
}

export function useServices(): AppServices {
  const ctx = useContext(AppServicesContext);
  if (!ctx) throw new Error("useServices must be used within <AppServicesProvider>");
  return ctx;
}
